import { ApiRequestError } from '../api';
import { isAbort, loadNews, loadNewsItem } from '../data';
import { formatDate, loc, t } from '../i18n';
import { navigate, setTitle } from '../router';
import { NEWS_CATEGORIES, type News, type NewsCategory } from '../types';
import { $, emptyState, errorState, h, link, loadingState, rebind, richText, svg, youtubeId } from '../ui/dom';
import { categoryLabel, newsCard } from '../ui/cards';
import { icons } from '../ui/icons';
import { openLightbox } from '../ui/overlay';

let controller: AbortController | null = null;
let sortDesc = true;

export function mountNews(params: URLSearchParams): void {
  controller?.abort();
  controller = new AbortController();
  const id = Number(params.get('id'));
  const listView = $('news-list-view');
  const detail = $('news-detail');
  const showDetail = Number.isInteger(id) && id > 0;
  listView?.classList.toggle('hidden', showDetail);
  detail?.classList.toggle('hidden', !showDetail);
  if (showDetail) void renderDetail(id, controller.signal);
  else void renderList(params, controller.signal);
}

export function unmountNews(): void {
  controller?.abort();
  controller = null;
}

function currentCategory(params: URLSearchParams): NewsCategory | null {
  const c = params.get('cat');
  return (NEWS_CATEGORIES as string[]).includes(c ?? '') ? (c as NewsCategory) : null;
}

async function renderList(params: URLSearchParams, signal: AbortSignal): Promise<void> {
  const list = $('news-list');
  const filters = $('news-filters');
  const count = $('news-count');
  const sortLabel = $('news-sort-label');
  if (!list || !filters) return;
  const cat = currentCategory(params);

  filters.replaceChildren(
    link('/news', { class: `chip${cat ? '' : ' active'}`, text: t('common.all') }),
    ...NEWS_CATEGORIES.map((c) =>
      link(`/news?cat=${c}`, { class: `chip chip-${c}${cat === c ? ' active' : ''}`, text: categoryLabel(c) }),
    ),
  );

  let items: News[] = [];
  const draw = (): void => {
    if (sortLabel) sortLabel.textContent = sortDesc ? t('common.newest') : t('common.oldest');
    const filtered = items
      .filter((n) => !cat || n.category === cat)
      .sort((a, b) => (sortDesc ? 1 : -1) * (Date.parse(b.published_at) - Date.parse(a.published_at)));
    if (count) count.textContent = `${filtered.length} ${t('common.records')}`;
    const total = filtered.length;
    list.replaceChildren(
      ...(filtered.length
        ? filtered.map((n, i) => newsCard(n, sortDesc ? total - i : i + 1))
        : [emptyState()]),
    );
  };

  rebind('news-sort', 'click', () => {
    sortDesc = !sortDesc;
    draw();
  });
  if (sortLabel) sortLabel.textContent = sortDesc ? t('common.newest') : t('common.oldest');

  list.replaceChildren(loadingState());
  try {
    items = await loadNews(signal);
    if (signal.aborted) return;
    draw();
  } catch (err) {
    if (!isAbort(err)) list.replaceChildren(errorState(() => void renderList(params, signal)));
  }
}

function embedFor(url: string): HTMLElement {
  const yt = youtubeId(url);
  if (yt) {
    return h(
      'div',
      'video-frame',
      h('iframe', {
        attrs: {
          src: `https://www.youtube-nocookie.com/embed/${yt}`,
          title: 'YouTube video',
          loading: 'lazy',
          allow: 'accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen',
          allowfullscreen: true,
          referrerpolicy: 'strict-origin-when-cross-origin',
        },
      }),
    );
  }
  return link(url, 'btn btn-ghost', svg(icons.play), t('news.video'), svg(icons.external));
}

async function renderDetail(id: number, signal: AbortSignal): Promise<void> {
  const root = $('news-detail');
  if (!root) return;
  root.replaceChildren(loadingState());

  let item: News;
  try {
    item = await loadNewsItem(id, signal);
  } catch (err) {
    if (isAbort(err)) return;
    if (err instanceof ApiRequestError && err.status === 404) {
      root.replaceChildren(
        h('div', 'state state-empty', h('span', { text: t('news.notFound') }), link('/news', { class: 'btn btn-ghost btn-sm', text: t('news.toList') })),
      );
    } else {
      root.replaceChildren(errorState(() => void renderDetail(id, signal)));
    }
    return;
  }
  if (signal.aborted) return;

  const title = loc(item, 'title');
  setTitle(title);
  const summary = loc(item, 'summary');

  const back = h(
    'button',
    { class: 'back-link mono', attrs: { type: 'button' }, on: { click: () => navigate('/news') } },
    svg(icons.arrowLeft),
    t('news.toList'),
  );

  const gallery = item.gallery?.length
    ? h(
        'section',
        'detail-section',
        h('h2', { class: 'section-label mono', text: `// ${t('news.gallery')}` }),
        h(
          'div',
          'gallery',
          ...item.gallery.map((src) =>
            h('button', { class: 'gallery-item', attrs: { type: 'button' }, on: { click: () => openLightbox(src, title) } }, h('img', { attrs: { src, alt: '', loading: 'lazy' } })),
          ),
        ),
      )
    : null;

  const links = item.links?.length
    ? h(
        'section',
        'detail-section',
        h('h2', { class: 'section-label mono', text: `// ${t('news.links')}` }),
        h('ul', 'detail-links', ...item.links.map((l) => h('li', null, link(l.url, 'inline-link', svg(icons.external), l.label)))),
      )
    : null;

  const relatedBox = h('section', 'detail-section related');

  const parts: (HTMLElement | null)[] = [
    back,
    h(
      'header',
      'detail-head',
      h(
        'div',
        'tech-line',
        link(`/news?cat=${item.category}`, { class: `tag tag-${item.category}`, text: categoryLabel(item.category) }),
        h('span', 'rule'),
        h('time', { class: 'mono', attrs: { datetime: item.published_at }, text: formatDate(item.published_at, true) }),
      ),
      h('h1', { text: title }),
      summary ? h('p', { class: 'lead', text: summary }) : null,
    ),
    item.cover_url ? h('figure', 'detail-cover', h('img', { attrs: { src: item.cover_url, alt: '' } })) : null,
    richText(loc(item, 'body'), 'rich detail-body'),
    item.video_url ? h('section', 'detail-section', h('h2', { class: 'section-label mono', text: `// ${t('news.video')}` }), embedFor(item.video_url)) : null,
    gallery,
    links,
    relatedBox,
  ];
  root.replaceChildren(...parts.filter((n): n is HTMLElement => n !== null));

  try {
    const all = await loadNews(signal);
    if (signal.aborted) return;
    const related = all
      .filter((n) => n.id !== item.id)
      .sort((a, b) => Number(b.category === item.category) - Number(a.category === item.category))
      .slice(0, 3);
    if (related.length) {
      relatedBox.replaceChildren(
        h('h2', { class: 'section-label mono', text: `// ${t('news.related')}` }),
        h('div', 'news-grid news-grid-compact', ...related.map((n) => newsCard(n))),
      );
    }
  } catch {
    /* related posts are optional */
  }
}
